import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tab,
  TabList,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Card,
  CardHeader,
  CardPreview,
} from '@fluentui/react-components';
import {
  AddRegular,
  EditRegular,
  DeleteRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { Tag, CreateTagDTO } from '../../../models/tag';
import { SystemOption } from '../../../models/systemOptions';
import { SupportQuestion } from '../../../models/supportQuestion';
import { UserSystemData } from '../../../models/users';
import { Module } from '../../../models/module';
import { deepCloneObject } from '../../../utils/clone';
import { useAppContext } from '../../../AppContext';
import { createTag, deleteTag, getAllTags, updateTag } from '../../../services/tagService';
import { getSystemOptionsByID, updateSystemOption } from '../../../services/systemOptionService';
import { updateTenderingDocument } from '../../../services/tenderingService';
import SystemTendering from './SystemComponents/SystemTendering';
import SystemTenders from './SystemComponents/SystemTenders';
import SystemManagers from './SystemComponents/SystemManagers';
import TagCreateEditDialog from './SystemDialogs/TagCreateEditDialog';
import TagDeleteDialog from './SystemDialogs/TagDeleteDialog';
import SystemPasswordDialog from './SystemDialogs/SystemPasswordDialog';
import TemplateEditorDialog from './SystemDialogs/TemplateEditorDialog';
// import { getSupportQuestions as getAllSupportQuestions} from '../../../services/supportQuestionService';
// import SystemTendering from '../system-tendering/SystemTendering';
// import SystemTenders from '../system-tenders/SystemTenders';
// import TagCreateEditDialog from './TagCreateEditDialog';
// import TagDeleteDialog from './TagDeleteDialog';
// import SystemPasswordDialog from './SystemPasswordDialog';
// import SupportQuestionDialog from './SupportQuestionDialog';
// import TemplateEditorDialog from './TemplateEditorDialog';
// import ConfirmationDialog from '../../shared/ConfirmationDialog';
// import NotificationDialog from '../../shared/NotificationDialog';
// import ErrorDialog from '../../shared/ErrorDialog';

interface Template {
  name: string;
  text: string;
}

interface SystemData {
  templates: Template[];
}

interface SystemProps {
  isOpen: boolean;
  onClose: () => void;
}

const System: React.FC<SystemProps> = ({ isOpen, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [modules, setModules] = useState<Module[]>([]);
  const [tenderingOptions, setTenderingOptions] = useState<any[]>([]);
  const [tenderOptions, setTenderOptions] = useState<any[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [systemOptions, setSystemOptions] = useState<SystemOption | null>(null);
  const [systemOptionsInitial, setSystemOptionsInitial] = useState<SystemOption | null>(null);
  const [supportQuestions, setSupportQuestions] = useState<SupportQuestion[]>([]);
  const [system, setSystem] = useState<SystemData>({ templates: [] });
  const [template, setTemplate] = useState<Template>({ name: '', text: '' });
  const [templateLoading, setTemplateLoading] = useState(false);

  const {user} = useAppContext();

  // Dialog states
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagDialogMode, setTagDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [tagDeleteDialogOpen, setTagDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<SupportQuestion | null>(null);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState<any>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  useEffect(() => {
    if(isOpen){
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      const modulesData = user.modules;
      setModules(modulesData);
      setTenderingOptions(modulesData.filter((m: Module) => m.moduleType === 'Tendering'));
      setTenderOptions(modulesData.filter((m: Module) => m.moduleType === 'Tender'));

      await Promise.all([
        getTags(),
        getSystemOptions(),
        getSupportQuestions(),
      ]);

      setLoaded(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const getTags = async () => {
    try {
      const tagsData = await getAllTags();
      setTags(tagsData);
      setTagsLoading(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const getSystemOptions = async () => {
    try {
      const options = await getSystemOptionsByID(1);
      setSystemOptions(options);
      setSystemOptionsInitial(deepCloneObject(options));
      setSystemLoaded(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const getSupportQuestions = async () => {
    try {
      // const questions = await getAllSupportQuestions();
      setSupportQuestions([]);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const onTagCreate = () => {
    setTagDialogMode('create');
    setSelectedTag(null);
    setTagDialogOpen(true);
  };

  const onTagEdit = (tag: Tag) => {
    setTagDialogMode('edit');
    setSelectedTag(tag);
    setTagDialogOpen(true);
  };

  const onTagDelete = (tag: Tag) => {
    setSelectedTag(tag);
    setTagDeleteDialogOpen(true);
  };

  const handleTagSave = async (tagName: string) => {
    try {
      setTagsLoading(true);
      const newTag: CreateTagDTO = { tagName };
      
      if (tagDialogMode === 'create') {
        await createTag(newTag)
      } else if (selectedTag) {
        await updateTag(selectedTag.tagID, newTag)
      }
      
      await getTags();
      setTagDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const handleTagDeleteConfirm = async () => {
    if (!selectedTag) return;
    
    try {
      setTagsLoading(true);
      await deleteTag(selectedTag.tagID)
      setTags(tags.filter(t => t.tagID !== selectedTag.tagID));
      setTagsLoading(false);
      setTagDeleteDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const systemOptionsHasChanges = (): boolean => {
    if (!systemOptions || !systemOptionsInitial) return false;
    return systemOptions.supportTicketLink !== systemOptionsInitial.supportTicketLink ||
      systemOptions.placementReportLink !== systemOptionsInitial.placementReportLink ||
      systemOptions.billingLink !== systemOptionsInitial.billingLink ||
      systemOptions.supportQuestionLink !== systemOptionsInitial.supportQuestionLink;
  };

  const updateSysOptions = async () => {
    if (!systemOptions) return;
    
    try {
      await updateSystemOption(1, systemOptions);
      setSystemOptionsInitial(deepCloneObject(systemOptions));
      setNotificationMessage('Systeminställningar sparade!');
      setNotificationOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      if (systemOptionsInitial) {
        setSystemOptions(systemOptionsInitial);
      }
    }
  };

  const openSystemPasswordModal = () => {
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = async (newPassword: string) => {
    if (!systemOptions) return;
    
    try {
      const updatedOptions = { ...systemOptions, systemPassword: newPassword };
      await updateSystemOption(1, updatedOptions);
      setSystemOptions(updatedOptions);
      setPasswordDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const createSupportQuestion = () => {
    setSelectedQuestion(null);
    setQuestionDialogOpen(true);
  };

  const editSupportQuestion = (question: SupportQuestion) => {
    setSelectedQuestion(question);
    setQuestionDialogOpen(true);
  };

  const handleQuestionSave = async (questionData: any) => {
    try {
      // if (selectedQuestion) {
      //   await supportQuestionService.updateSupportQuestion(selectedQuestion.supportQuestionID, questionData).toPromise();
      // } else {
      //   await supportQuestionService.createSupportQuestion(questionData).toPromise();
      // }
      await getSupportQuestions();
      setQuestionDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const deleteSupportQuestion = (question: SupportQuestion) => {
    setConfirmDialogData({
      title: 'Radera fråga?',
      text: 'Vill du radera denna fråga?',
      onConfirm: async () => {
        try {
          // await supportQuestionService.deleteSupportQuestion(question.supportQuestionID).toPromise();
          await getSupportQuestions();
          setConfirmDialogOpen(false);
        } catch (error) {
          console.error(error);
          setErrorDialogOpen(true);
        }
      }
    });
    setConfirmDialogOpen(true);
  };

  const openHTMLEditor = () => {
    setTemplateEditorOpen(true);
  };

  const saveSupportTemplate = async () => {
    if (!systemOptions) return;
    
    try {
      setTemplateLoading(true);
      await updateSystemOption(1, systemOptions);
      const updatedOptions = await getSystemOptionsByID(1);
      setSystemOptions(updatedOptions);
      setSystemOptionsInitial(deepCloneObject(updatedOptions));
      setTemplateLoading(false);
      setTemplateEditorOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      setTemplateLoading(false);
    }
  };

  const onTemplateChange = (selectedTemplate: Template) => {
    setTemplate(deepCloneObject(selectedTemplate));
  };

  const saveTemplate = async () => {
    try {
      // const newTemplates = [...system.templates];
      // const existingIndex = newTemplates.findIndex(t => t.name === template.name);
      
      // if (existingIndex >= 0) {
      //   newTemplates[existingIndex] = deepCloneObject(template);
      // } else {
      //   newTemplates.push(deepCloneObject(template));
      // }
      
      // const updatedSystem = { ...system, templates: newTemplates };
      // setSystemLoaded(false);
      
      // const result = await updateTenderingDocument(-1, updatedSystem).toPromise();
      // setSystem({ ...system, templates: result.templates });
      // setTemplate({ name: '', text: '' });
      // setSystemLoaded(true);
      // setNotificationMessage('Systeminställningar sparat!');
      // setNotificationOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      setSystemLoaded(true);
    }
  };

  const onTemplateDelete = async () => {
    // try {
    //   const newTemplates = system.templates.filter(t => t.name !== template.name);
    //   const updatedSystem = { ...system, templates: newTemplates };
    //   setSystemLoaded(false);
      
    //   await moduleService.updateTenderingDocument(-1, updatedSystem).toPromise();
    //   setSystem(updatedSystem);
    //   setTemplate({ name: '', text: '' });
    //   setSystemLoaded(true);
    // } catch (error) {
    //   console.error(error);
    //   setErrorDialogOpen(true);
    //   setSystemLoaded(true);
    // }
  };

  // if (!loaded || !systemLoaded) {
  //   return (
  //     <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
  //       <DialogSurface className="max-w-[95vw] max-h-[95vh]">
  //         <DialogBody>
  //           <div className="flex items-center justify-center h-96">
  //             <Spinner size="large" label="Laddar..." />
  //           </div>
  //         </DialogBody>
  //       </DialogSurface>
  //     </Dialog>
  //   );
  // }

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ minWidth: '80vw', height: '95vh', display: 'flex', flexDirection: 'column' }}>
        <DialogBody style={{ height: '90vh' }}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            System
          </DialogTitle>
          <DialogContent className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-hidden flex flex-col">
            <TabList selectedValue={activeTab} onTabSelect={(_, data) => setActiveTab(data.value as string)}>
              <Tab value="general">Allmän</Tab>
              {systemLoaded && <Tab value="tendering">Upphandlingar</Tab>}
              {systemLoaded && <Tab value="tenders">Verksamheter</Tab>}
              {systemLoaded && <Tab value="managers">Ramavtalansvariga</Tab>}
            </TabList>

            <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'general' && (
            <div className="grid grid-cols-1 gap-6 max-w-7xl">
              {/* Tags Section */}
              <Card className="shadow-sm">
                <CardPreview>
                  <h3 className="text-xl font-semibold">Systeminställningar</h3>
                </CardPreview>

                <div>
                  <div className="overflow-hidden rounded-lg border border-gray-200 mb-6">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 items-center">
                          <TableHeaderCell className="font-semibold">Etikett namn</TableHeaderCell>
                          <TableHeaderCell className='text-right w-32'>
                            {tagsLoading ? (
                              <Spinner size="small" />
                            ) : (
                              <Button
                                size='small'
                                shape='circular'
                                appearance="primary"
                                icon={<AddRegular />}
                                onClick={onTagCreate}
                                title="Skapa ny etikett"
                              />
                            )}
                          </TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tags.map((tag, index) => (
                          <TableRow 
                            key={tag.tagID}
                            className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-50'}
                          >
                            <TableCell>{tag.tagName}</TableCell>
                            <TableCell className="text-right w-32">
                              {tagsLoading ? (
                                <Spinner size="small" />
                              ) : tag.isSystemTag ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                  System
                                </span>
                              ) : (
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    appearance="subtle"
                                    icon={<EditRegular />}
                                    onClick={() => onTagEdit(tag)}
                                    title="Ändra etikett"
                                    className="hover:bg-blue-50 hover:text-blue-600"
                                  />
                                  <Button
                                    appearance="subtle"
                                    icon={<DeleteRegular />}
                                    onClick={() => onTagDelete(tag)}
                                    title="Radera etikett"
                                    className="hover:bg-red-50 hover:text-red-600"
                                  />
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {systemOptions && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Lime ärende länk</h4>
                        <Input
                          className="w-full"
                          value={systemOptions.supportTicketLink || ''}
                          onChange={(e) => setSystemOptions({ ...systemOptions, supportTicketLink: e.target.value })}
                        />
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Placering rapportering länk</h4>
                        <Input
                          className="w-full"
                          value={systemOptions.placementReportLink || ''}
                          onChange={(e) => setSystemOptions({ ...systemOptions, placementReportLink: e.target.value })}
                        />
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Rapport fakturering länk</h4>
                        <Input
                          className="w-full"
                          value={systemOptions.billingLink || ''}
                          onChange={(e) => setSystemOptions({ ...systemOptions, billingLink: e.target.value })}
                        />
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Support kontakt länk</h4>
                        <Input
                          className="w-full"
                          value={systemOptions.supportQuestionLink || ''}
                          onChange={(e) => setSystemOptions({ ...systemOptions, supportQuestionLink: e.target.value })}
                        />
                      </div>

                      <Button
                        appearance="primary"
                        disabled={!systemOptionsHasChanges()}
                        onClick={updateSysOptions}
                      >
                        Spara ändringar
                      </Button>

                      <div className="pt-4">
                        <h4 className="text-sm font-semibold mb-2">System lösenord</h4>
                        <Button appearance="primary" onClick={openSystemPasswordModal}>
                          Redigera system lösenord
                        </Button>
                      </div>

                      {/* <div className="pt-4">
                        <h4 className="text-sm font-semibold mb-2">Upphandlade tjänster</h4>
                        <Button appearance="primary" onClick={openHTMLEditor}>
                          Redigera mall
                        </Button>
                      </div> */}
                    </div>
                  )}
                </div>
              </Card>

              {/* Support Questions Section */}
              {/* <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold">Support frågor</h3>
                </div>

                <div className="space-y-4 mb-6">
                  {supportQuestions.map((q) => (
                    <div key={q.supportQuestionID} className="flex justify-between items-start p-4 border border-gray-200 rounded">
                      <div className="flex-1">
                        <h4 className="text-base font-semibold mb-2">{q.header}</h4>
                        <div dangerouslySetInnerHTML={{ __html: q.answer }} />
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          appearance="subtle"
                          icon={<EditRegular />}
                          onClick={() => editSupportQuestion(q)}
                          title="Ändra fråga"
                        />
                        <Button
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          onClick={() => deleteSupportQuestion(q)}
                          title="Radera fråga"
                          className="text-red-600 hover:text-red-700"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <Button appearance="primary" onClick={createSupportQuestion}>
                  SKAPA FRÅGA
                </Button>
              </div> */}

              {/* Templates Section */}
              {/* <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold">Mallar</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Välj Mall</h4>
                    <Dropdown
                      placeholder="Välj Mall"
                      value={template.name || ''}
                      onOptionSelect={(_, data) => {
                        const selected = system.templates.find(t => t.name === data.optionValue);
                        if (selected) onTemplateChange(selected);
                      }}
                    >
                      {system.templates.map((t) => (
                        <Option key={t.name} value={t.name}>
                          {t.name}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold mb-2">Mall namn</h5>
                    <Input
                      className="w-full"
                      value={template.name || ''}
                      onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold mb-2">Text</h5>
                    <textarea
                      className="w-full border border-gray-300 rounded p-2 min-h-[180px]"
                      value={template.text || ''}
                      onChange={(e) => setTemplate({ ...template, text: e.target.value })}
                    />
                  </div>

                  <div className="flex gap-4 items-center">
                    <Button appearance="primary" onClick={saveTemplate}>
                      SPARA MALL
                    </Button>
                    <Button
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      onClick={onTemplateDelete}
                      title="Radera mall"
                      className="text-red-600 hover:text-red-700"
                    />
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {activeTab === 'tendering' && systemLoaded && user && (
            <SystemTendering
              systemPassword={systemOptions?.systemPassword || ''}
              user={user}
              tenderingOptions={tenderingOptions}
            />
          )}

          {activeTab === 'tenders' && systemLoaded && user && (
            <SystemTenders
              systemPassword={systemOptions?.systemPassword || ''}
              user={user}
              tenderingOptions={tenderingOptions}
            />
          )}

          {activeTab === 'managers' && systemLoaded && (
            <SystemManagers
              tenderingOptions={tenderingOptions}
            />
          )}
            </div>
          </div>
        </DialogContent>
          {/* Dialogs */}
          {tagDialogOpen && (
        <TagCreateEditDialog
          isOpen={tagDialogOpen}
          onClose={() => setTagDialogOpen(false)}
          onSave={handleTagSave}
          mode={tagDialogMode}
          initialValue={selectedTag?.tagName || ''}
        />
      )}

      {tagDeleteDialogOpen && selectedTag && (
        <TagDeleteDialog
          isOpen={tagDeleteDialogOpen}
          onClose={() => setTagDeleteDialogOpen(false)}
          onConfirm={handleTagDeleteConfirm}
          tagName={selectedTag.tagName}
        />
      )}

      {passwordDialogOpen && user && (
        <SystemPasswordDialog
          isOpen={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onSave={handlePasswordChange}
          user={user.user.email}
        />
      )}

      {/* {questionDialogOpen && (
        <SupportQuestionDialog
          isOpen={questionDialogOpen}
          onClose={() => setQuestionDialogOpen(false)}
          onSave={handleQuestionSave}
          question={selectedQuestion}
        />
      )} */}

      {/* {templateEditorOpen && systemOptions && (
        <TemplateEditorDialog
          isOpen={templateEditorOpen}
          onClose={() => {
            setTemplateEditorOpen(false);
            if (systemOptionsInitial) {
              setSystemOptions({ ...systemOptions, supportContactHtmlTemplate: systemOptionsInitial.supportContactHtmlTemplate });
            }
          }}
          onSave={saveSupportTemplate}
          template={systemOptions.supportContactHtmlTemplate}
          onTemplateChange={(value) => setSystemOptions({ ...systemOptions, supportContactHtmlTemplate: value })}
          loading={templateLoading}
        />
      )} */}

      {/* {confirmDialogOpen && confirmDialogData && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={confirmDialogData.onConfirm}
          title={confirmDialogData.title}
          text={confirmDialogData.text}
        />
      )}

      {notificationOpen && (
        <NotificationDialog
          isOpen={notificationOpen}
          onClose={() => setNotificationOpen(false)}
          message={notificationMessage}
        />
      )}

          {errorDialogOpen && (
            <ErrorDialog
              isOpen={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
            />
          )} */}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default System;
